// Author: Ahsan Saleem
/**
 * The Bloom mark — the face of BloomOS. An owned rosette that expresses the
 * system's state through form, motion AND an accompanying label (never colour
 * alone). It is the equivalent of the Apple menu or Ubuntu circle: the
 * recognisable centre of the operating environment.
 */
export type BloomState = 'healthy' | 'attention' | 'critical' | 'sim' | 'offline' | 'loading' | 'idle';

const STATE_TEXT: Record<BloomState, string> = {
  healthy: 'All systems within limits',
  attention: 'Attention needed',
  critical: 'Critical condition',
  sim: 'Simulated facility',
  offline: 'Offline',
  loading: 'Loading',
  idle: 'BloomOS',
};

export function Bloom({ size = 24, state = 'idle', title, className = '' }: {
  size?: number; state?: BloomState; title?: string; className?: string;
}) {
  const petals = [0, 45, 90, 135, 180, 225, 270, 315];
  return (
    <span className={`bloommark bm-${state} ${className}`} style={{ width: size, height: size }}
      role="img" aria-label={title ?? STATE_TEXT[state]}>
      <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden focusable="false">
        <g className="bm-petals">
          {petals.map((r, i) => (
            <ellipse key={r} className="bm-petal" cx="24" cy="12.5" rx="3.4" ry="10.4"
              transform={`rotate(${r} 24 24)`} style={{ animationDelay: `${i * 90}ms` }} />
          ))}
        </g>
        <circle className="bm-ring" cx="24" cy="24" r="6.6" />
        <circle className="bm-core" cx="24" cy="24" r="3.2" />
      </svg>
    </span>
  );
}

/** The wordmark lock-up: mark + Cormorant "BloomOS". */
export function BloomWordmark({ state = 'idle' }: { state?: BloomState }) {
  return (
    <span className="bloomword">
      <Bloom size={26} state={state} />
      <span className="bloomword-t">BloomOS</span>
    </span>
  );
}
